import { useQuery } from '@apollo/client';
import { Fragment, useEffect, useState } from 'react';
import InfiniteScroll from 'react-infinite-scroller';
import { GET_POSTS } from '../../helpers/graphql/posts';
import { getUser } from '../../helpers/token';
import { MUTED } from '../Landing/ui';
import StartBlogCta from '../Post/Article/StartBlogCta';
import BlogPostCard from '../Blog/BlogPostCard';
import BadgeCard from './BadgeCard';
import ItinerariesList from './ItinerariesList';

const PAGE_SIZE = 12;
const GRID =
  'tw-grid tw-grid-cols-1 tw-gap-x-6 tw-gap-y-10 sm:tw-grid-cols-2 lg:tw-grid-cols-3';

function SkeletonRow({ count = 3 }) {
  return (
    <div className={`${GRID} tw-mt-10`}>
      {Array.from({ length: count }, (_, i) => (
        <div
          key={i}
          className="tw-overflow-hidden tw-rounded-2xl tw-border tw-border-solid tw-border-[#E7E0CF] dark:tw-border-[#26382F] tw-bg-white dark:tw-bg-[#15231E]"
        >
          <div className="tw-aspect-[4/3] tw-animate-pulse tw-bg-[#EDE7D8] dark:tw-bg-[#26382F]" />
          <div className="tw-p-4">
            <div className="tw-h-4 tw-w-4/5 tw-animate-pulse tw-rounded tw-bg-[#EDE7D8] dark:tw-bg-[#26382F]" />
            <div className="tw-mt-3 tw-h-3 tw-w-2/5 tw-animate-pulse tw-rounded tw-bg-[#EDE7D8] dark:tw-bg-[#26382F]" />
          </div>
        </div>
      ))}
    </div>
  );
}

export default function BlogTab({ profile, itineraries }) {
  const author = profile.author.username;
  const roles = profile.author.roles || [];
  // Paying members' profiles are their product — no self-promos on them.
  const isPaidAuthor = roles.indexOf('paid') !== -1;

  const [hasMore, setHasMore] = useState(true);
  const [postsLength, setPostsLength] = useState(PAGE_SIZE);
  // The grid only renders client-side (query is ssr:false), so checking
  // membership here is hydration-safe — unlike in SSR'd markup.
  const [isMember, setIsMember] = useState(true);

  useEffect(() => {
    setIsMember(!!getUser());
  }, []);

  // Client-side navigation between author pages re-renders in place.
  useEffect(() => {
    setHasMore(true);
    setPostsLength(PAGE_SIZE);
  }, [author]);

  const { data, loading, fetchMore } = useQuery(GET_POSTS, {
    variables: {
      author,
      limit: PAGE_SIZE,
      orderby: 'createdAt',
      useBlacklist: false,
    },
    ssr: false,
    skip: !!itineraries,
  });

  if (itineraries) {
    return (
      <div className="tw-bg-[#FAF8F1] dark:tw-bg-[#0E1A16]">
        <div className="tw-mx-auto tw-max-w-5xl tw-px-4 tw-py-10">
          <ItinerariesList initialItineraries={itineraries} />
        </div>
      </div>
    );
  }

  const posts = (data && data.posts) || [];
  if (!loading && posts.length < PAGE_SIZE && hasMore) setHasMore(false);

  const loadMore = () => {
    if (postsLength === posts.length) {
      fetchMore({
        variables: { offset: posts.length, limit: PAGE_SIZE },
        updateQuery: (prev, { fetchMoreResult }) => {
          if (!fetchMoreResult) return prev;
          if (fetchMoreResult.posts.length < PAGE_SIZE) setHasMore(false);
          return { ...prev, posts: [...prev.posts, ...fetchMoreResult.posts] };
        },
      });
      setPostsLength(postsLength + PAGE_SIZE);
    } else if (postsLength !== posts.length + PAGE_SIZE) {
      setPostsLength(posts.length);
    }
  };

  return (
    <div className="tw-min-h-[400px] tw-bg-[#FAF8F1] dark:tw-bg-[#0E1A16]">
      <div className="tw-mx-auto tw-max-w-5xl tw-px-4 tw-py-10">
        <BadgeCard user={author} />

        {loading && posts.length === 0 ? (
          <div className={GRID}>
            {Array.from({ length: 6 }, (_, i) => (
              <div
                key={i}
                className="tw-overflow-hidden tw-rounded-2xl tw-border tw-border-solid tw-border-[#E7E0CF] dark:tw-border-[#26382F] tw-bg-white dark:tw-bg-[#15231E]"
              >
                <div className="tw-aspect-[4/3] tw-animate-pulse tw-bg-[#EDE7D8] dark:tw-bg-[#26382F]" />
                <div className="tw-p-4">
                  <div className="tw-h-4 tw-w-4/5 tw-animate-pulse tw-rounded tw-bg-[#EDE7D8] dark:tw-bg-[#26382F]" />
                  <div className="tw-mt-3 tw-h-3 tw-w-2/5 tw-animate-pulse tw-rounded tw-bg-[#EDE7D8] dark:tw-bg-[#26382F]" />
                </div>
              </div>
            ))}
          </div>
        ) : posts.length === 0 ? (
          <p className={`${MUTED} tw-py-16 tw-text-center tw-text-[15px]`}>
            @{author} hasn&apos;t started blogging yet.
          </p>
        ) : (
          <InfiniteScroll
            loadMore={loadMore}
            hasMore={hasMore}
            threshold={1200}
            loader={<SkeletonRow key="loader" />}
          >
            <div className={GRID}>
              {posts.map((post, i) => (
                <Fragment key={`${post.author.username}-${post.permlink}`}>
                  <BlogPostCard post={post} />
                  {/* Woven into the grid after the first page — with infinite
                      scroll, anything below the grid is never reached. */}
                  {i === PAGE_SIZE - 1 && !isPaidAuthor && !isMember && (
                    <div className="sm:tw-col-span-2 lg:tw-col-span-3">
                      <StartBlogCta
                        thumbUrl={posts[0].thumb ? posts[0].thumb.url : undefined}
                      />
                    </div>
                  )}
                </Fragment>
              ))}
            </div>
          </InfiniteScroll>
        )}

        {/* Authors with less than a page of posts: CTA after the list ends. */}
        {!hasMore &&
          !isPaidAuthor &&
          !isMember &&
          posts.length > 0 &&
          posts.length < PAGE_SIZE && (
            <StartBlogCta
              thumbUrl={posts[0].thumb ? posts[0].thumb.url : undefined}
            />
          )}
      </div>
    </div>
  );
}
